import React, { useEffect, useRef, useState } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import { TrackTable } from '../components/TrackTable';
import { useLibrary } from '../contexts/LibraryContext';
import { searchSuggestions } from '../data/seeds';
import { searchTracks } from '../utils/itunes';
import type { Track } from '../types/music';
export function Search() {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Track[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const {
    recentSearches,
    rememberSearch
  } = useLibrary();
  useEffect(() => {
    const query = term.trim();
    if (query.length < 2) {
      setResults([]);
      setStatus('idle');
      return;
    }
    setStatus('loading');
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        const tracks = await searchTracks(query, 25, controller.signal);
        setResults(tracks);
        setStatus('ready');
        rememberSearch(query);
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setStatus('error');
      }
    }, 350);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [rememberSearch, term]);
  const chips = term.trim().length >= 2 ? [] : recentSearches.length ? recentSearches : searchSuggestions;
  return <div className="px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-404" data-mp="3m5p5l8u6j2xs">
      <h1 className="font-display text-4xl text-cream" data-id="element-405" data-mp="3r25vo8kxvrdi">Search the catalogue</h1>
      <p className="mt-2 text-sm text-muted" data-id="element-406" data-mp="3w2adq31hwu5u">Live results from the iTunes Search API as you type.</p>

      <div className="relative mt-6 max-w-xl" data-id="element-407" data-mp="3xzu3rt26hgq">
        <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-408" data-mp="3zsyf0g78plk" />
        <input ref={inputRef} type="search" value={term} onChange={e => setTerm(e.target.value)} placeholder="Artists, songs, moods…" aria-label="Search for music" className="w-full rounded-full border border-ink-line bg-ink-panel py-3 pl-11 pr-11 text-sm text-cream placeholder:text-muted focus:border-gold/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-409" data-mp="1be5xbqq89bga" />
        {term && <button type="button" onClick={() => {
        setTerm('');
        inputRef.current?.focus();
      }} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-muted transition-colors duration-150 ease-night hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-410" data-mp="3gdjiq5ijpy52">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-411" data-mp="29zakbg6ohnre" />
          </button>}
      </div>

      {chips.length > 0 && <div className="mt-5 flex flex-wrap gap-2" data-id="element-412" data-mp="24zfttdqtf06v">
          {chips.map(chip => <button key={chip} type="button" onClick={() => setTerm(chip)} className="rounded-full border border-ink-line px-3.5 py-1.5 text-xs text-muted transition-colors duration-150 ease-night hover:border-gold/60 hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-413" data-mp="ki6jrk2rzmk9">
              {chip}
            </button>)}
        </div>}

      <div className="mt-10" aria-live="polite" data-id="element-414" data-mp="144b97fi5ozl2">
        {status === 'loading' && <ul className="space-y-3" data-id="element-415" data-mp="1877y1nyahrxg">
            {Array.from({
          length: 6
        }).map((_, i) => <li key={i} className="flex items-center gap-3" data-id="element-416" data-mp="u7q83599c8mi" data-mpi={i}>
                <span className="h-10 w-10 animate-pulse rounded bg-ink-raised" data-id="element-417" data-mp="z6jl6wkmtxm5" />
                <span className="h-3 w-52 animate-pulse rounded bg-ink-raised" data-id="element-418" data-mp="3eyql92kn09yx" />
              </li>)}
          </ul>}
        {status === 'error' && <p className="rounded-sm border border-ink-line bg-ink-panel px-4 py-3 text-sm text-cream" data-id="element-419" data-mp="3jvh41xnttjo1">
            That search didn&apos;t come back. Try again in a moment.
          </p>}
        {status === 'ready' && results.length === 0 && <p className="text-sm text-muted" data-id="element-420" data-mp="3qfyaob0qf55m">Nothing in the crates for “{term.trim()}”.</p>}
        {status === 'ready' && results.length > 0 && <div className="overflow-x-auto scroll-thin" data-id="element-421" data-mp="m0opqvbbsp8l">
            <TrackTable tracks={results} data-id="element-422" data-mp="lzdj8nqhx3ds" />
          </div>}
      </div>
    </div>;
}